// src/Components/Layout/Header.jsx
import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Header = () => {
    const { t } = useTranslation();
    const [selectedMenuName, setSelectedMenuName] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const updateMenuName = () => {
        const menuName = localStorage.getItem('selectedMenuName') || '';
        setSelectedMenuName(menuName);
    };

    // Initial load
    useEffect(() => {
        updateMenuName();
    }, []);

    // Listen for storage changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'selectedMenuName') {
                setSelectedMenuName(e.newValue || '');
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Custom event listener voor directe updates
    useEffect(() => {
        const handleMenuChange = () => {
            updateMenuName();
        };

        window.addEventListener('menuChanged', handleMenuChange);
        return () => {
            window.removeEventListener('menuChanged', handleMenuChange);
        };
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('expiresIn');
        localStorage.removeItem('selectedMenu');
        localStorage.removeItem('selectedMenuName');
        navigate('/');
    };

    const navLinks = [
        { to: "/dashboard", label: t('navigation.home') },
        { to: "/categories", label: t('navigation.categories') },
        { to: "/dishes", label: t('navigation.dishes') },
        { to: "/products", label: t('navigation.products') },
        { to: "/more", label: t('navigation.more') }
    ];

    return (
        <header className="w-full fixed bg-white shadow-sm z-50">
            <div className="max-w-[1100px] mx-auto px-4">
                {/* Desktop Header */}
                <div className="hidden md:flex justify-between items-center py-4">
                    <h1 className="text-2xl font-normal font-inter text-ps-blue-dark">
                        PS IN FOODSERVICE
                    </h1>
                    <div className="flex items-center gap-4">
                        {selectedMenuName && (
                            <span className="text-ps-blue-dark font-inter">
                                {selectedMenuName}
                            </span>
                        )}
                        <LanguageSelector />
                        <button
                            onClick={handleLogout}
                            className="p-2 text-ps-blue hover:text-ps-blue-dark transition-colors"
                            title={t('navigation.logout')}
                        >
                            <LogOut size={24} />
                        </button>
                    </div>
                </div>

                {/* Mobile Header */}
                <div className="md:hidden">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex flex-col">
                            <h1 className="text-xl font-normal font-inter text-ps-blue-dark">
                                PS IN FOODSERVICE
                            </h1>
                            {selectedMenuName && (
                                <span className="text-ps-blue-dark font-inter text-sm mt-1">
                                    {selectedMenuName}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <LanguageSelector />
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 text-ps-blue hover:text-ps-blue-dark"
                            >
                                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex gap-10 pb-4">
                    {navLinks.map(link => (
                        <Link
                            key={link.to}
                            to={link.to}
                            className="text-ps-blue hover:text-ps-blue-dark transition-colors"
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <nav className="md:hidden py-4 flex flex-col space-y-4 border-t">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className="text-ps-blue hover:text-ps-blue-dark transition-colors block"
                            >
                                {link.label}
                            </Link>
                        ))}
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-ps-blue hover:text-ps-blue-dark transition-colors"
                        >
                            <LogOut size={20} />
                            <span>{t('navigation.logout')}</span>
                        </button>
                    </nav>
                )}
            </div>
            <div className="h-[10px] line-background" />
        </header>
    );
};

export default Header;